import type { PanelConfig, WidgetType } from '../../types';
import { lazy, Suspense, type ComponentType } from 'react';

// Widget props interface
export interface WidgetComponentProps {
  panel: PanelConfig;
  dark?: boolean;
  isEditMode?: boolean;
}

// Lazy load widgets for code splitting
const widgetComponents: Record<WidgetType, ComponentType<WidgetComponentProps>> = {
  weather: lazy(() => import('./Weather')),
  calendar: lazy(() => import('./Calendar')),
  clock: lazy(() => import('./Clock')),
  'drive-time': lazy(() => import('./DriveTime')),
  'sun-moon': lazy(() => import('./SunMoon')),
  aqi: lazy(() => import('./Aqi')),
  uv: lazy(() => import('./Uv')),
  kasa: lazy(() => import('./Kasa')),
  wol: lazy(() => import('./Wol')),
  brightness: lazy(() => import('./Brightness')),
  iframe: lazy(() => import('./Iframe')),
  climate: lazy(() => import('./Climate')),
  timer: lazy(() => import('./Timer')),
  ptt: lazy(() => import('./Ptt')),
  mqtt: lazy(() => import('./Mqtt')),
  adguard: lazy(() => import('./Adguard')),
  notifications: lazy(() => import('./Notifications')),
  schedule: lazy(() => import('./Schedule')),
  'iframe-popup': lazy(() => import('./IframePopup')),
  'system-stats': lazy(() => import('./SystemStats')),
  money: lazy(() => import('./Money')),
  'battery-status': lazy(() => import('./BatteryStatus')),
  'private-mode': lazy(() => import('./PrivateMode')),
  planes: lazy(() => import('./Planes')),
};

// Loading placeholder
function WidgetLoading() {
  return (
    <div className="w-full h-full flex items-center justify-center text-text-muted">Loading...</div>
  );
}

// Widget renderer component
export function WidgetRenderer({ panel, dark, isEditMode }: WidgetComponentProps) {
  const Widget = widgetComponents[panel.widget];

  if (!Widget) {
    return (
      <div className="w-full h-full flex items-center justify-center text-danger">
        Unknown widget: {panel.widget}
      </div>
    );
  }

  return (
    <Suspense fallback={<WidgetLoading />}>
      <Widget panel={panel} dark={dark} isEditMode={isEditMode} />
    </Suspense>
  );
}
